import React, { useState, useEffect } from 'react';
import api from '../api';
import { FaChartPie, FaBox, FaDollarSign, FaCalculator, FaExclamationTriangle } from 'react-icons/fa';

const StockValueReport = () => {
    const [produtos, setProdutos] = useState([]);
    const [valorTotalEstoque, setValorTotalEstoque] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [viewMode, setViewMode] = useState('cards');
    const [isMobile, setIsMobile] = useState(false);
    const [filtroValor, setFiltroValor] = useState('todos'); // 'todos', 'alto', 'medio', 'baixo'

    // Detectar se é mobile
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
            if (window.innerWidth <= 768) {
                setViewMode('cards');
            } else {
                setViewMode('table');
            }
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        carregarDadosEstoque();
    }, []);

    const carregarDadosEstoque = async () => {
        try {
            setLoading(true);
            const response = await api.get('/valor-estoque');
            setProdutos(response.data.produtos);
            setValorTotalEstoque(response.data.valor_total_estoque);
            setError(null);
        } catch (error) {
            console.error("Erro ao carregar dados do estoque:", error);
            setError("Erro ao carregar dados do estoque");
        } finally {
            setLoading(false);
        }
    };

    // Categorizar produtos por valor
    const categorizarPorValor = (produto) => {
        if (!produto.valor_total) return 'baixo';
        const valor = produto.valor_total;
        if (valor >= 1000) return 'alto';
        if (valor >= 100) return 'medio';
        return 'baixo';
    };

    // Filtrar produtos
    const produtosFiltrados = produtos
        .filter(produto => produto.estoque_atual > 0)
        .filter(produto => {
            if (filtroValor === 'todos') return true;
            return categorizarPorValor(produto) === filtroValor;
        })
        .sort((a, b) => (b.valor_total || 0) - (a.valor_total || 0));

    // Componente Card para Produto
    const ProductCard = ({ produto }) => {
        const categoria = categorizarPorValor(produto);
        const cores = {
            alto: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700' },
            medio: { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-700' },
            baixo: { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-700' }
        };
        const cor = cores[categoria] || cores.baixo;

        return (
            <div className={`rounded-lg border ${cor.border} ${cor.bg} p-4 mb-4`}>
                <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                        <h3 className="font-semibold text-gray-800">{produto.nome}</h3>
                        <div className="text-sm text-gray-500 mt-1">
                            ID: {produto.id}
                        </div>
                    </div>
                    <div className={`px-3 py-1 rounded-full text-xs font-semibold ${cor.bg} ${cor.text} border ${cor.border}`}>
                        {categoria === 'alto' ? 'Alto' : categoria === 'medio' ? 'Médio' : 'Baixo'}
                    </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    <div className="bg-white rounded-lg p-3 border border-gray-100">
                        <div className="flex items-center gap-1 text-gray-500 text-xs mb-1">
                            <FaBox size={10} />
                            <span>Estoque</span>
                        </div>
                        <div className="font-bold text-gray-800">{produto.estoque_atual}</div>
                    </div>
                    
                    <div className="bg-white rounded-lg p-3 border border-gray-100">
                        <div className="flex items-center gap-1 text-gray-500 text-xs mb-1">
                            <FaDollarSign size={10} />
                            <span>Preço</span>
                        </div>
                        <div className="font-bold text-gray-800 text-sm">
                            {produto.preco_custo ? `R$ ${produto.preco_custo.toFixed(2)}` : "-"}
                        </div>
                    </div>
                    
                    <div className="col-span-2 sm:col-span-1 bg-white rounded-lg p-3 border border-gray-100">
                        <div className="flex items-center gap-1 text-gray-500 text-xs mb-1">
                            <FaCalculator size={10} />
                            <span>Valor Total</span>
                        </div>
                        <div className={`font-bold text-lg ${cor.text}`}>
                            {produto.valor_total ? `R$ ${produto.valor_total.toFixed(2)}` : "-"}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="max-w-full md:max-w-6xl mx-auto mt-10 bg-white p-4 md:p-8 rounded-lg shadow-lg">
                <div className="text-center py-8">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                    <p className="mt-2 text-gray-600">Calculando valor do estoque...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="max-w-full md:max-w-6xl mx-auto mt-10 bg-white p-4 md:p-8 rounded-lg shadow-lg">
                <div className="text-center py-8">
                    <FaExclamationTriangle className="mx-auto text-red-500 mb-2" size={32} />
                    <p className="text-red-600 font-medium">{error}</p>
                    <button 
                        onClick={() => window.location.reload()}
                        className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                    >
                        Tentar Novamente
                    </button>
                </div>
            </div>
        );
    }

    // Estatísticas para os badges
    const stats = {
        total: produtosFiltrados.length,
        alto: produtosFiltrados.filter(p => categorizarPorValor(p) === 'alto').length,
        medio: produtosFiltrados.filter(p => categorizarPorValor(p) === 'medio').length,
        baixo: produtosFiltrados.filter(p => categorizarPorValor(p) === 'baixo').length
    };

    return (
        <div className="max-w-full md:max-w-6xl mx-auto mt-10 bg-white p-4 md:p-8 rounded-lg shadow-lg">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
                <div>
                    <h2 className="text-xl md:text-2xl font-semibold text-gray-700 mb-2 md:mb-0">
                        Valor do Estoque
                    </h2>
                    <div className="flex flex-wrap gap-2 text-sm text-gray-600">
                        <span className="bg-gray-100 px-2 py-1 rounded">
                            {stats.total} produtos
                        </span>
                        {stats.alto > 0 && (
                            <span className="bg-green-100 text-green-700 px-2 py-1 rounded">
                                {stats.alto} alto valor
                            </span>
                        )}
                        {stats.medio > 0 && (
                            <span className="bg-yellow-100 text-yellow-700 px-2 py-1 rounded">
                                {stats.medio} médio valor
                            </span>
                        )}
                    </div>
                </div>

                {!isMobile && (
                    <div className="flex gap-2 mt-4 md:mt-0">
                        <button
                            onClick={() => setViewMode('table')}
                            className={`px-4 py-2 rounded-lg ${viewMode === 'table' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'}`}
                        >
                            Tabela
                        </button>
                        <button
                            onClick={() => setViewMode('cards')}
                            className={`px-4 py-2 rounded-lg ${viewMode === 'cards' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'}`}
                        >
                            Cards
                        </button>
                    </div>
                )}
            </div>

            {/* Valor Total em Destaque */}
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-2">
                        <FaChartPie className="text-green-600" size={20} />
                        <span className="text-gray-700 font-medium">Valor Total do Estoque:</span>
                    </div>
                    <span className="text-xl md:text-2xl font-bold text-green-600">
                        R$ {valorTotalEstoque.toFixed(2)}
                    </span>
                </div>
            </div>

            {/* Filtros */}
            <div className="mb-4 flex flex-wrap gap-2">
                <button
                    onClick={() => setFiltroValor('todos')}
                    className={`px-4 py-2 rounded-lg ${filtroValor === 'todos' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'}`}
                >
                    Todos
                </button>
                <button
                    onClick={() => setFiltroValor('alto')}
                    className={`px-4 py-2 rounded-lg ${filtroValor === 'alto' ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-700'}`}
                >
                    Alto Valor (≥ R$ 1000)
                </button>
                <button
                    onClick={() => setFiltroValor('medio')}
                    className={`px-4 py-2 rounded-lg ${filtroValor === 'medio' ? 'bg-yellow-500 text-white' : 'bg-gray-200 text-gray-700'}`}
                >
                    Médio Valor (R$ 100-999)
                </button>
                <button
                    onClick={() => setFiltroValor('baixo')}
                    className={`px-4 py-2 rounded-lg ${filtroValor === 'baixo' ? 'bg-gray-500 text-white' : 'bg-gray-200 text-gray-700'}`}
                >
                    Baixo Valor (&lt; R$ 100)
                </button>
            </div>

            {/* Visualização Condicional */}
            {produtosFiltrados.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                    <FaBox className="mx-auto mb-2" size={24} />
                    <p>Nenhum produto no estoque com os filtros selecionados</p>
                </div>
            ) : viewMode === 'table' ? (
                <div className="overflow-x-auto -mx-4 md:mx-0">
                    <div className="inline-block min-w-full align-middle px-4 md:px-0">
                        <table className="min-w-full border-collapse border border-gray-300">
                            <thead>
                                <tr className="bg-gray-100">
                                    <th className="p-2 md:p-3 text-left border border-gray-300">Produto</th>
                                    <th className="p-2 md:p-3 text-center border border-gray-300">Estoque</th>
                                    <th className="p-2 md:p-3 text-center border border-gray-300 md:table-cell hidden">Preço</th>
                                    <th className="p-2 md:p-3 text-center border border-gray-300">Valor Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {produtosFiltrados.map((produto, index) => (
                                    <tr key={index} className="border border-gray-300 hover:bg-gray-50">
                                        <td className="p-2 md:p-3 border border-gray-300">
                                            <div>
                                                <div className="font-medium">{produto.nome}</div>
                                                <div className="text-xs text-gray-500 md:hidden">
                                                    Preço: {produto.preco_custo ? `R$ ${produto.preco_custo.toFixed(2)}` : "-"}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="p-2 md:p-3 text-center border border-gray-300">{produto.estoque_atual}</td>
                                        <td className="p-2 md:p-3 text-center border border-gray-300 md:table-cell hidden">
                                            {produto.preco_custo ? `R$ ${produto.preco_custo.toFixed(2)}` : "-"}
                                        </td>
                                        <td className="p-2 md:p-3 text-center border border-gray-300 font-bold text-green-600">
                                            {produto.valor_total ? `R$ ${produto.valor_total.toFixed(2)}` : "-"}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : (
                <div className="space-y-4">
                    {produtosFiltrados.map((produto) => (
                        <ProductCard key={produto.id} produto={produto} />
                    ))}
                </div>
            )}
        </div>
    );
};

export default StockValueReport;